import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Bell, BellOff, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { useNotifications } from "../context/NotificationContext";
import { NotificationCard, NotificationItem } from "./shared/NotificationCard";
import {
  NOTIFICATION_TYPES,
  isNotificationEnabled,
  setNotificationEnabled,
  isSessionDismissed,
  dismissForSession,
  isItemDismissedForDate,
  dismissItemForDate,
  filterDismissedItems,
  getTodayString,
} from "../lib/notificationUtils";

const NOTIFICATION_TYPE = NOTIFICATION_TYPES.MISSING_REPORTS;

/**
 * Alert banner showing jobs missing daily reports
 * Displays on Dashboard for Admin/PM/Office Manager
 */
export const MissingReportsAlert = () => {
  const { missingReports, permissionStatus, requestPermission, loading } = useNotifications();
  const [expanded, setExpanded] = useState(false);
  const [sessionDismissed, setSessionDismissed] = useState(() => isSessionDismissed(NOTIFICATION_TYPE));
  const [dismissedJobs, setDismissedJobs] = useState({});

  // Filter out jobs that have been permanently dismissed for today
  const visibleJobs = filterDismissedItems(NOTIFICATION_TYPE, missingReports, "job_id")
    .filter(job => !dismissedJobs[job.job_id]);

  // Handle session dismiss (dismiss all until next login)
  const handleSessionDismiss = () => {
    dismissForSession(NOTIFICATION_TYPE);
    setSessionDismissed(true);
  };

  // Handle permanent dismiss for a specific job on today's date
  const handleDismissJob = (jobId) => {
    dismissItemForDate(NOTIFICATION_TYPE, jobId);
    setDismissedJobs(prev => ({ ...prev, [jobId]: true }));
  };

  // Check if should render
  if (!isNotificationEnabled(NOTIFICATION_TYPE) || sessionDismissed || loading || visibleJobs.length === 0) {
    return null;
  }

  const displayedJobs = expanded ? visibleJobs : visibleJobs.slice(0, 3);
  const hasMore = visibleJobs.length > 3;

  return (
    <NotificationCard
      variant="warning"
      icon={<AlertTriangle className="w-5 h-5" />}
      title={`${visibleJobs.length} Active Job${visibleJobs.length > 1 ? "s" : ""} Missing Today's Daily Report`}
      onDismissSession={handleSessionDismiss}
      testId="missing-reports-alert"
    >
      {/* Job list with individual dismiss buttons */}
      <div className="space-y-2">
        {displayedJobs.map((job) => (
          <NotificationItem
            key={job.job_id}
            onDismiss={() => handleDismissJob(job.job_id)}
            dismissLabel="No Work Today"
            dismissTitle="No work on this job today - dismiss permanently for this date"
            testId={`dismiss-job-${job.job_id}`}
          >
            <Link
              to={`/jobs/${job.job_id}`}
              className="text-sm text-amber-700 hover:text-amber-900 hover:underline"
              data-testid={`missing-job-${job.job_id}`}
            >
              {job.job_name} {job.client_name && <span className="text-amber-600">({job.client_name})</span>}
            </Link>
          </NotificationItem>
        ))}
      </div>
      
      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-2 h-auto p-0 text-amber-600 hover:text-amber-800"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <>Show Less <ChevronUp className="w-4 h-4 ml-1" /></>
          ) : (
            <>Show {visibleJobs.length - 3} More <ChevronDown className="w-4 h-4 ml-1" /></>
          )}
        </Button>
      )}

      {/* Notification permission prompt */}
      {permissionStatus === "default" && (
        <div className="mt-3 pt-3 border-t border-amber-200">
          <Button
            variant="outline"
            size="sm"
            className="text-amber-700 border-amber-300 hover:bg-amber-100"
            onClick={requestPermission}
            data-testid="enable-notifications-btn"
          >
            <Bell className="w-4 h-4 mr-2" />
            Enable 3:45 PM Reminders
          </Button>
        </div>
      )}

      {permissionStatus === "denied" && (
        <div className="mt-3 pt-3 border-t border-amber-200 flex items-center gap-2 text-xs text-amber-600">
          <BellOff className="w-3 h-3" />
          Browser notifications blocked. Enable in browser settings for daily reminders.
        </div>
      )}
    </NotificationCard>
  );
};

/**
 * Compact alert for foreman's Daily Entry page
 */
export const DailyEntryAlert = ({ jobs = [] }) => {
  const { missingReports } = useNotifications();
  const [sessionDismissed, setSessionDismissed] = useState(() => isSessionDismissed(NOTIFICATION_TYPE));
  const [dismissedJobs, setDismissedJobs] = useState({});
  
  // Filter to only show jobs the foreman can enter for, excluding dismissed ones
  const relevantMissing = missingReports.filter(report => {
    const isRelevant = jobs.some(job => job.id === report.job_id);
    const isDismissed = isItemDismissedForDate(NOTIFICATION_TYPE, report.job_id) || dismissedJobs[report.job_id];
    return isRelevant && !isDismissed;
  });

  const handleSessionDismiss = () => {
    dismissForSession(NOTIFICATION_TYPE);
    setSessionDismissed(true);
  };

  const handleDismissJob = (jobId) => {
    dismissItemForDate(NOTIFICATION_TYPE, jobId);
    setDismissedJobs(prev => ({ ...prev, [jobId]: true }));
  };

  if (!isNotificationEnabled(NOTIFICATION_TYPE) || sessionDismissed || relevantMissing.length === 0) {
    return null;
  }

  return (
    <div 
      className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4"
      data-testid="daily-entry-alert"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-amber-800">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm font-medium">
            {relevantMissing.length === 1 
              ? "No daily report submitted yet"
              : `${relevantMissing.length} jobs need today's daily report`
            }
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs text-amber-600 hover:text-amber-800 hover:bg-amber-100"
          onClick={handleSessionDismiss}
          data-testid="daily-entry-dismiss-btn"
        >
          Dismiss
        </Button>
      </div>
      <div className="space-y-1 ml-6">
        {relevantMissing.map(job => (
          <div key={job.job_id} className="flex items-center justify-between text-xs">
            <span className="text-amber-700">• {job.job_name}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 px-1 text-xs text-gray-500 hover:text-red-600"
              onClick={() => handleDismissJob(job.job_id)}
              title="No work today"
            >
              ×
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Notification permission prompt (standalone)
 */
export const NotificationPrompt = () => {
  const { permissionStatus, requestPermission } = useNotifications();
  const [dismissed, setDismissed] = useState(false);

  // Hide if dismissed or if permission has been acted upon (granted or denied)
  if (dismissed || permissionStatus !== "default") {
    return null;
  }

  const handleEnable = async () => {
    await requestPermission();
    // Always dismiss after clicking Enable, regardless of permission result
    // This prevents the banner from staying if browser blocks the prompt
    setDismissed(true);
  };

  return (
    <Card className="border-blue-200 bg-blue-50" data-testid="notification-prompt">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <Bell className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-blue-800">Enable Daily Report Reminders</h3>
            <p className="text-sm text-blue-600 mt-1">
              Get notified at 3:45 PM if any active jobs are missing daily reports.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDismissed(true)}
              data-testid="notification-prompt-later"
            >
              Later
            </Button>
            <Button
              size="sm"
              onClick={handleEnable}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="notification-prompt-enable"
            >
              Enable
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Export utility functions for Settings page
export const getNotificationsEnabled = () => isNotificationEnabled(NOTIFICATION_TYPE);
export const setNotificationsEnabledExport = (enabled) => setNotificationEnabled(NOTIFICATION_TYPE, enabled);
